import { useContext, FC, useMemo } from 'react';
import { AuthContext } from '@repo-level/core-front';

import { BonanzaContext } from 'contexts/bonanza';
import { BonanzaPromoSection } from './BonanzaSection';
import { StarDustRushPanel } from './StarDustRushPanel';
import { groupMember, vipGroups } from 'helpers/user';
import { StardustRushContext } from 'contexts/stardustRush/stardustRush';

import { ScPromoPanels } from './styled';
import { AuroraPanel } from './AuroraPanel';
import { useAuroraCrystalAvailable } from 'modules/crystal/hooks';
import { QuestPanel } from './QuestPanel';
import { useUserCanAccessQuestPromo } from 'modules/common/hooks';

interface PanelProps {
  onLinkClick?: () => void;
}

export const PromoPanelsDesktop = () => {
  const { isBonanzaPromoAvailable } = useContext(BonanzaContext);
  const { user, pendingUser } = useContext(AuthContext);
  const { isStardustRushAvailable, stardustRushCMSData } = useContext(
    StardustRushContext
  );

  const isVip = useMemo(() => {
    return vipGroups.some((group) => groupMember(group)(user)) && !pendingUser;
  }, [user, pendingUser]);

  const isStardustRush =
    isStardustRushAvailable && stardustRushCMSData && isVip;

  const { isAuroraCrystalVisible } = useAuroraCrystalAvailable();
  const isQuestPromoVisible = useUserCanAccessQuestPromo();

  const isPanelsDesktop =
    isBonanzaPromoAvailable ||
    isStardustRush ||
    isAuroraCrystalVisible ||
    isQuestPromoVisible;

  if (!isPanelsDesktop) return null;

  return (
    <>
      <div className="divider" />

      <ScPromoPanels>
        {isBonanzaPromoAvailable && <BonanzaPromoSection />}
        {isStardustRush && <StarDustRushPanel />}
        {isAuroraCrystalVisible && <AuroraPanel />}
        {isQuestPromoVisible && <QuestPanel />}
      </ScPromoPanels>
    </>
  );
};

export const PromoPanelsMobile: FC<PanelProps> = ({ onLinkClick }) => {
  const { isBonanzaPromoAvailable } = useContext(BonanzaContext);
  const { user, pendingUser } = useContext(AuthContext);
  const { isStardustRushAvailable } = useContext(StardustRushContext);

  const isVip = useMemo(
    () => vipGroups.some((group) => groupMember(group)(user)) && !pendingUser,
    [user, pendingUser]
  );

  const isStardustRush = isStardustRushAvailable && isVip;

  const { isAuroraCrystalVisible } = useAuroraCrystalAvailable();
  const isQuestPromoVisible = useUserCanAccessQuestPromo();

  const isPanelsMobile =
    isBonanzaPromoAvailable ||
    isStardustRush ||
    isAuroraCrystalVisible ||
    isQuestPromoVisible;

  if (!isPanelsMobile) return null;

  return (
    <ScPromoPanels isMobile>
      {isBonanzaPromoAvailable && (
        <BonanzaPromoSection isMobile onLinkClick={onLinkClick} />
      )}
      {isStardustRush && (
        <StarDustRushPanel isMobile onLinkClick={onLinkClick} />
      )}
      {isAuroraCrystalVisible && <AuroraPanel onLinkClick={onLinkClick} />}
      {isQuestPromoVisible && <QuestPanel onLinkClick={onLinkClick} />}
    </ScPromoPanels>
  );
};
